/**
 * iter-372 — Annexure Table for Edit BOQ.
 *
 * A per-BOQ "Annexure — Material, Brand & Installation Specifications"
 * grid. Each row corresponds to a BOQ line item and displays six
 * editable fields (Scope / Material / Installation / Inclusions /
 * Exclusions / Assumptions) parsed from the item's description.
 *
 * Bi-directional sync (contract with parent BOQEditPage):
 *   - Parent passes `items` (the current editItems array).
 *   - On any field edit we call `onItemPatch(actualIndex, { description: <re-serialized> })`.
 *     Parent updates `editItems[actualIndex].description` accordingly.
 *   - Parent re-renders → this component re-parses the description and
 *     shows the latest state. So editing the raw description textarea
 *     upstream ALSO updates the annexure cells here in real-time.
 *
 * iter-375 — Adds AI Auto-Rewrite for legacy items whose descriptions
 * don't yet follow the 6-section format + XLSX/PDF exports.
 *
 * iter-377 — Adds:
 *   • Legend filter chips (one per Main Category present in the BOQ) —
 *     clicking a chip filters BOTH the annexure grid AND the parent
 *     BOQ table (via `categoryFilter` / `onCategoryFilterChange`).
 *   • Two independent public-visibility toggles ("Show on Client link"
 *     + "Show on Vendor link"), persisted on the worksheet by the
 *     parent Save button.
 *   • Row checkboxes + a "Rewrite Selected" button so admins can
 *     re-run the AI on a specific subset (any range or ad-hoc pick).
 *   • A "Force re-rewrite" toggle that bypasses the "already rewritten"
 *     skip guard.
 *   • Idempotency: rows rewritten in the current session get a small
 *     purple "AI ✓" badge and are skipped by the full-sweep button
 *     unless Force is on.
 */
import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, FileText, Filter, Sparkles, Loader2,
  Download, FileSpreadsheet, XCircle, Check, Eye, EyeOff,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CreditBadge } from '@/components/CreditBadge';
import { parseDescription, serializeDescription, ANNEXURE_SECTIONS } from '@/utils/descAnnexure';
import { markdownHtmlProps } from '@/utils/mdRender';
import { highlightHtml } from '@/components/boq/HighlightedText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEADER_COL_STYLE = 'px-2 py-2 text-left text-[11px] font-semibold text-stone-700 border-b border-stone-200 bg-stone-50';

export default function AnnexureTable({
  items = [],
  onItemPatch,
  worksheetId,
  projectSegment,
  // iter-377 — legend filter, public toggles, idempotent-rewrite props.
  categoryFilter = '',
  onCategoryFilterChange,
  showToClient = false,
  onShowToClientChange,
  showToVendor = false,
  onShowToVendorChange,
  aiRewrittenIds,
  onMarkRewritten,
  // iter-380 — live Find highlight from FindReplaceBar. When non-empty,
  // every occurrence of `findQuery` inside cell content is wrapped in
  // a soft-amber <mark>. Auto-clears when the Find bar closes.
  findQuery = '',
  findCaseSensitive = false,
}) {
  const [expanded, setExpanded] = useState(true);
  const [textFilter, setTextFilter] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { actualIndex, key }
  const [draft, setDraft] = useState('');
  // iter-375 — bulk-rewrite + export state
  const [rewriting, setRewriting] = useState(false);
  const [exporting, setExporting] = useState('');
  // iter-376 — persistent progress + cancel for bulk-rewrite
  const [rewriteProgress, setRewriteProgress] = useState(null);
  // iter-377 — row selection + force toggle for selective rewrite
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [forceRewrite, setForceRewrite] = useState(false);

  const rewrittenSet = aiRewrittenIds instanceof Set ? aiRewrittenIds : new Set();
  const rowId = (it) => it?.id || it?._editId || '';

  // A row is "legacy" if the description doesn't contain any of the 6
  // canonical section headings — iter-379 uses parseDescription's
  // non-enumerable `hasHeadings` flag so the Scope-fallback (used on
  // public views) doesn't mask legacy items in the Edit BOQ annexure.
  const isLegacyDesc = (desc) => {
    if (!desc || !desc.trim()) return true;
    const parsed = parseDescription(desc);
    return !parsed.hasHeadings;
  };

  // iter-377 — collect main categories present in the BOQ for the legend
  // chips. { code, name, count } sorted by code so the legend is stable.
  const legendChips = useMemo(() => {
    const map = new Map();
    (items || []).forEach((it) => {
      if (!it || it.item_type === 'header') return;
      const code = String(it.main_category_code || '').toUpperCase();
      if (!code) return;
      const cur = map.get(code) || { code, name: it.main_category_name || code, count: 0 };
      cur.count += 1;
      map.set(code, cur);
    });
    return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [items]);

  // Only rows that are real line items (skip category headers).
  // iter-377 — respect the shared category filter first, then the local
  // text search. Category filter is source of truth for both the BOQ table
  // and this grid.
  const rows = useMemo(() => {
    const q = textFilter.trim().toLowerCase();
    const cat = String(categoryFilter || '').toUpperCase();
    return items
      .map((it, idx) => ({ item: it, actualIndex: idx }))
      .filter(({ item }) => item && item.item_type !== 'header' && (item.task_title || item.description))
      .filter(({ item }) => {
        if (!cat) return true;
        const cc = String(item.main_category_code || item.category_code || '').toUpperCase();
        return cc === cat || cc.startsWith(cat);
      })
      .filter(({ item }) => {
        if (!q) return true;
        return (
          (item.task_title || '').toLowerCase().includes(q) ||
          (item.description || '').toLowerCase().includes(q) ||
          (item.main_category_name || '').toLowerCase().includes(q)
        );
      });
  }, [items, textFilter, categoryFilter]);

  const applyCellEdit = (actualIndex, key, newVal) => {
    const existing = parseDescription(items[actualIndex]?.description || '');
    const nextAnnex = { ...existing, [key]: newVal };
    const nextDesc = serializeDescription(nextAnnex);
    onItemPatch?.(actualIndex, { description: nextDesc });
  };

  const commitDraft = () => {
    if (!editingCell) return;
    applyCellEdit(editingCell.actualIndex, editingCell.key, draft);
    setEditingCell(null);
    setDraft('');
  };
  const cancelDraft = () => {
    setEditingCell(null);
    setDraft('');
  };

  // iter-377 — Row selection helpers.
  const toggleSelect = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => {
    setSelectedIds(new Set(rows.map(({ item }) => rowId(item)).filter(Boolean)));
  };

  // iter-377 — Shared rewrite runner. `pool` is the exact list of
  // {it, idx} pairs the caller wants rewritten. `force` bypasses the
  // "already rewritten" skip. Chunked into serial batches of 8 so each
  // LLM call finishes inside the ingress timeout; each chunk uses an
  // AbortController so the admin can cancel mid-flight.
  const runRewrite = async (pool, force) => {
    if (!worksheetId) { toast.error('Worksheet id missing'); return; }
    if (!pool.length) { toast.info('Nothing to rewrite'); return; }
    setRewriting(true);
    const CHUNK = 8;
    let totalTouched = 0;
    const progress = { done: 0, total: pool.length, currentTitles: [], controller: null, cancelled: false };
    setRewriteProgress(progress);
    const touchedIds = [];
    try {
      for (let start = 0; start < pool.length; start += CHUNK) {
        if (progress.cancelled) break;
        const slice = pool.slice(start, start + CHUNK);
        progress.currentTitles = slice.map(({ it }) => it.task_title || '(untitled)');
        progress.controller = new AbortController();
        setRewriteProgress({ ...progress });
        const r = await axios.post(
          `${API}/boq-worksheets/${worksheetId}/ai-bulk-rewrite-descriptions`,
          {
            items: slice.map(({ it }) => ({
              id: rowId(it),
              task_title: it.task_title || '',
              description: it.description || '',
              main_category_name: it.main_category_name || '',
              sub_category_name: it.sub_category_name || '',
              quantity: Number(it.quantity) || 1,
              unit: it.unit || 'NOS',
            })),
            project_segment: projectSegment || 'residential_apartment',
            force: !!force,
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 90000,
            signal: progress.controller.signal,
          },
        );
        if (progress.cancelled) break;
        const results = r.data?.rewritten || [];
        results.forEach((res, i) => {
          const orig = slice[i];
          if (!orig || !res || !res.description || res.description === orig.it.description) return;
          onItemPatch?.(orig.idx, { description: res.description });
          totalTouched += 1;
          const id = rowId(orig.it);
          if (id) touchedIds.push(id);
        });
        progress.done = Math.min(pool.length, start + slice.length);
        setRewriteProgress({ ...progress });
      }
      if (touchedIds.length) onMarkRewritten?.(touchedIds);
      if (progress.cancelled) {
        toast.info(`Cancelled — kept ${totalTouched} rewrite${totalTouched !== 1 ? 's' : ''} already applied`);
      } else if (totalTouched > 0) {
        toast.success(`Rewrote ${totalTouched} description${totalTouched !== 1 ? 's' : ''} — remember to Save`);
      } else {
        toast.info('AI did not produce material updates');
      }
    } catch (e) {
      if (axios.isCancel?.(e) || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
        toast.info(`Cancelled — kept ${totalTouched} rewrite${totalTouched !== 1 ? 's' : ''} already applied`);
      } else {
        toast.error(e.response?.data?.detail || 'AI rewrite failed mid-batch — earlier items may still be updated');
      }
    } finally {
      setRewriting(false);
      setRewriteProgress(null);
    }
  };

  // iter-375 — Bulk AI rewrite for legacy items. iter-377 — additionally
  // skip items already rewritten this session unless Force is on.
  const runBulkRewrite = async () => {
    const pool = items
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => it && it.item_type !== 'header' && (it.task_title || it.description))
      .filter(({ it }) => isLegacyDesc(it.description))
      .filter(({ it }) => forceRewrite || !rewrittenSet.has(rowId(it)));
    if (!pool.length) {
      toast.info(
        forceRewrite
          ? 'All items already use the 6-section format — nothing legacy to rewrite'
          : 'No legacy items pending — turn on Force to re-run rewritten rows',
      );
      return;
    }
    await runRewrite(pool, forceRewrite);
  };

  // iter-377 — Rewrite ONLY the checked rows. Force is implicit here:
  // user picked them deliberately, so we always send even if already
  // marked rewritten. (The Force toggle still flows to the backend for
  // audit consistency.)
  const runSelectiveRewrite = async () => {
    if (!selectedIds.size) { toast.info('Select at least one row first'); return; }
    const idSet = selectedIds;
    const pool = items
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => it && it.item_type !== 'header' && idSet.has(rowId(it)));
    if (!pool.length) { toast.info('Selected rows are no longer present'); return; }
    await runRewrite(pool, true);
    clearSelection();
  };

  // iter-386 — Tier update now runs in TWO passes:
  //   1) Call the backend once to get the AI proposals (no commit yet).
  //   2) Open a Diff modal showing old→new Material / Installation /
  //      Vendor rate per row. Admin can then Commit or Cancel.
  const [tierDiff, setTierDiff] = useState(null);  // { tier, rows: [{orig, proposal}] }

  const runTierUpdate = async (tier) => {
    if (!worksheetId) { toast.error('Worksheet id missing'); return; }
    if (!selectedIds.size) { toast.info('Select at least one row first'); return; }
    if (!['budget', 'standard', 'premium', 'luxury'].includes(tier)) return;
    const idSet = selectedIds;
    const pool = items
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => it && it.item_type !== 'header' && idSet.has(rowId(it)));
    if (!pool.length) { toast.info('Selected rows are no longer present'); return; }
    setRewriting(true);
    const CHUNK = 8;
    const all = [];
    try {
      for (let start = 0; start < pool.length; start += CHUNK) {
        const slice = pool.slice(start, start + CHUNK);
        const r = await axios.post(
          `${API}/boq-worksheets/${worksheetId}/ai-bulk-tier-update`,
          {
            items: slice.map(({ it }) => ({
              id: rowId(it),
              task_title: it.task_title || '',
              description: it.description || '',
              main_category_name: it.main_category_name || '',
              sub_category_name: it.sub_category_name || '',
              quantity: Number(it.quantity) || 1,
              unit: it.unit || 'NOS',
              vendor_rate: Number(it.vendor_rate) || 0,
              client_rate: Number(it.client_rate) || 0,
            })),
            tier,
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 90000 },
        );
        (r.data?.updated || []).forEach((res, i) => {
          const orig = slice[i];
          if (orig && res) all.push({ orig, proposal: res });
        });
      }
      if (!all.length) { toast.info('AI returned no changes'); return; }
      setTierDiff({ tier, rows: all });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tier update failed');
    } finally {
      setRewriting(false);
    }
  };

  // Extract just the Material / Installation section text from a
  // description so the diff can display them without the whole 6-section
  // block. Uses the shared parseDescription helper.
  const _sectionText = (desc, key) => {
    const parsed = parseDescription(desc || '');
    return (parsed[key] || '').trim() || '—';
  };

  const commitTierDiff = () => {
    if (!tierDiff) return;
    let touched = 0;
    tierDiff.rows.forEach(({ orig, proposal }) => {
      const patch = {};
      if (proposal.description) patch.description = proposal.description;
      if (proposal.vendor_rate != null) patch.vendor_rate = proposal.vendor_rate;
      if (Object.keys(patch).length) {
        onItemPatch?.(orig.idx, patch);
        touched += 1;
      }
    });
    const label = tierDiff.tier.charAt(0).toUpperCase() + tierDiff.tier.slice(1);
    toast.success(`Applied ${label} tier to ${touched} row${touched !== 1 ? 's' : ''} — remember to Save`);
    setTierDiff(null);
    clearSelection();
  };

  const cancelBulkRewrite = () => {
    if (!rewriteProgress) return;
    rewriteProgress.cancelled = true;
    try { rewriteProgress.controller?.abort(); } catch { /* ignore */ }
    setRewriteProgress({ ...rewriteProgress });
  };

  // iter-375 — download annexure as XLSX or PDF
  const doExport = async (kind) => {
    if (!worksheetId) { toast.error('Worksheet id missing'); return; }
    setExporting(kind);
    try {
      const r = await axios.get(
        `${API}/boq-worksheets/${worksheetId}/annexure-export/${kind}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob',
          timeout: 60000,
        },
      );
      const blob = new Blob([r.data], {
        type: kind === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Annexure.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Annexure ${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `${kind.toUpperCase()} export failed`);
    } finally {
      setExporting('');
    }
  };

  // iter-377 — legacy count now respects the "already rewritten" guard
  // so the bulk button doesn't tempt admins to re-run finished items.
  const legacyCount = useMemo(() => (
    items.filter((it) =>
      it && it.item_type !== 'header'
      && (it.task_title || it.description)
      && isLegacyDesc(it.description)
      && (forceRewrite || !rewrittenSet.has(rowId(it))),
    ).length
  ), [items, forceRewrite, rewrittenSet]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-stone-200 mt-4" data-testid="boq-annexure-panel">
      <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-stone-50 rounded-t-lg" data-testid="boq-annexure-header">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-left"
          data-testid="boq-annexure-toggle"
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <FileText size={16} className="text-amber-700" />
          <span className="font-semibold text-stone-900">
            Annexure — Material, Brand & Installation Specifications
          </span>
          <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded">
            {rows.length} line{rows.length !== 1 ? 's' : ''}
          </span>
          {legacyCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded" data-testid="annexure-legacy-count">
              {legacyCount} legacy
            </span>
          )}
        </button>
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          {selectedIds.size > 0 && (
            <>
              <span className="text-[11px] text-stone-500 mr-1" data-testid="annexure-selected-count">
                {selectedIds.size} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={runSelectiveRewrite}
                disabled={rewriting}
                data-testid="annexure-rewrite-selected-btn"
                className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                title="AI-rewrite ONLY the selected rows (force applied)"
              >
                <Sparkles size={12} className="mr-1 text-purple-600" />
                Rewrite Selected
                <CreditBadge toolKey="ai_bulk_rewrite" className="ml-1.5" />
              </Button>
              {/* iter-385 — Bulk Quality Tier update. AI re-writes only
                  Material Spec + Installation + Unit Rate; Scope /
                  Inclusions / Exclusions are preserved as-is. */}
              <select
                onChange={(e) => {
                  const t = e.target.value;
                  if (!t) return;
                  e.target.value = '';
                  runTierUpdate(t);
                }}
                disabled={rewriting}
                data-testid="annexure-tier-update-select"
                className="h-7 text-xs border border-amber-300 text-amber-700 rounded px-2 bg-white hover:bg-amber-50 disabled:opacity-50"
                title="Bulk update Quality Tier for the selected rows"
              >
                <option value="">Tier ▾</option>
                <option value="budget">Budget</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
                <option value="luxury">Luxury</option>
              </select>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="h-7 text-xs text-stone-500"
                data-testid="annexure-clear-selection-btn"
              >
                Clear
              </Button>
            </>
          )}
          {legacyCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={runBulkRewrite}
              disabled={rewriting}
              data-testid="annexure-bulk-rewrite-btn"
              className="h-7 text-xs"
              title={`AI-rewrite ${legacyCount} legacy item description${legacyCount !== 1 ? 's' : ''} in the 6-section Indian-standards format`}
            >
              {rewriting ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1 text-purple-600" />}
              {rewriting
                ? (rewriteProgress ? `Rewriting ${rewriteProgress.done} / ${rewriteProgress.total}…` : 'Rewriting…')
                : `Rewrite ${legacyCount} Legacy`}
              <CreditBadge toolKey="ai_bulk_rewrite" className="ml-1.5" />
            </Button>
          )}
          <label className="flex items-center gap-1 text-[11px] text-stone-600 pl-1 border-l border-stone-200 ml-1" title="Rewrite even items already rewritten this session">
            <input
              type="checkbox"
              checked={forceRewrite}
              onChange={(e) => setForceRewrite(e.target.checked)}
              className="h-3 w-3 accent-purple-600"
              data-testid="annexure-force-rewrite-toggle"
            />
            <span>Force</span>
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => doExport('xlsx')}
            disabled={!!exporting || rows.length === 0}
            data-testid="annexure-export-xlsx-btn"
            className="h-7 text-xs"
            title="Download Annexure as XLSX"
          >
            {exporting === 'xlsx' ? <Loader2 size={12} className="animate-spin mr-1" /> : <FileSpreadsheet size={12} className="mr-1 text-emerald-600" />}
            XLSX
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => doExport('pdf')}
            disabled={!!exporting || rows.length === 0}
            data-testid="annexure-export-pdf-btn"
            className="h-7 text-xs"
            title="Download Annexure as PDF"
          >
            {exporting === 'pdf' ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1 text-blue-600" />}
            PDF
          </Button>
        </div>
      </div>

      {/* iter-377 — public-visibility toggles. Two independent checkboxes so
          admin can share the annexure with the client only, vendor only,
          both, or neither. Persisted via the parent Save button. */}
      <div className="border-t border-stone-100 bg-stone-50/50 px-4 py-2 flex flex-wrap items-center gap-4" data-testid="annexure-visibility-row">
        <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer" title="Show the Annexure grid on the Public BOQ link shared with the client">
          <input
            type="checkbox"
            checked={showToClient}
            onChange={(e) => onShowToClientChange?.(e.target.checked)}
            className="h-3.5 w-3.5 accent-amber-600"
            data-testid="annexure-show-client-toggle"
          />
          {showToClient ? <Eye size={12} className="text-amber-600" /> : <EyeOff size={12} className="text-stone-400" />}
          <span>Show on Client link</span>
        </label>
        <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer" title="Show the Annexure grid on the Vendor Quote link">
          <input
            type="checkbox"
            checked={showToVendor}
            onChange={(e) => onShowToVendorChange?.(e.target.checked)}
            className="h-3.5 w-3.5 accent-amber-600"
            data-testid="annexure-show-vendor-toggle"
          />
          {showToVendor ? <Eye size={12} className="text-amber-600" /> : <EyeOff size={12} className="text-stone-400" />}
          <span>Show on Vendor link</span>
        </label>
        <span className="text-[10px] text-stone-400 ml-auto">
          Remember to Save BOQ to persist the visibility choice.
        </span>
      </div>

      {/* iter-377 — legend filter chips. Clicking a chip filters BOTH this
          grid AND the parent BOQ table above. Clicking again clears. */}
      {legendChips.length > 0 && (
        <div className="border-t border-stone-100 px-4 py-2 flex flex-wrap items-center gap-1.5" data-testid="annexure-legend-chips">
          <span className="text-[11px] text-stone-500 mr-1">Filter by category:</span>
          {legendChips.map((chip) => {
            const active = String(categoryFilter || '').toUpperCase() === chip.code;
            return (
              <button
                key={chip.code}
                type="button"
                onClick={() => onCategoryFilterChange?.(active ? '' : chip.code)}
                className={
                  active
                    ? 'text-[10px] font-medium px-2 py-0.5 rounded-full border border-amber-500 bg-amber-500 text-white'
                    : 'text-[10px] font-medium px-2 py-0.5 rounded-full border border-stone-300 bg-white text-stone-700 hover:bg-amber-50 hover:border-amber-300'
                }
                data-testid={`annexure-legend-chip-${chip.code}`}
                title={`${chip.name} (${chip.count} item${chip.count !== 1 ? 's' : ''})`}
              >
                {chip.code} · {chip.name}
                <span className="ml-1 text-[9px] opacity-70">{chip.count}</span>
              </button>
            );
          })}
          {categoryFilter && (
            <button
              type="button"
              onClick={() => onCategoryFilterChange?.('')}
              className="text-[10px] text-stone-500 hover:text-red-600 ml-1"
              data-testid="annexure-legend-clear-btn"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* iter-376 — persistent progress bar for bulk rewrite. */}
      {rewriting && rewriteProgress && (
        <div className="border-t border-b border-purple-200 bg-purple-50/40 px-4 py-3" data-testid="annexure-rewrite-progress">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 text-sm text-purple-900">
              <Sparkles size={14} className="text-purple-600 animate-pulse" />
              <span className="font-medium">AI is rewriting descriptions</span>
              <span className="text-xs text-purple-700" data-testid="annexure-rewrite-progress-count">
                {rewriteProgress.done} / {rewriteProgress.total} done
              </span>
              {rewriteProgress.cancelled && (
                <span className="text-xs text-red-600 font-medium">— cancelling…</span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={cancelBulkRewrite}
              disabled={rewriteProgress.cancelled}
              data-testid="annexure-rewrite-cancel-btn"
              className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              title="Cancel remaining chunks — items already rewritten will stay applied"
            >
              <XCircle size={12} className="mr-1" />
              {rewriteProgress.cancelled ? 'Cancelling' : 'Cancel'}
            </Button>
          </div>
          <div className="w-full bg-purple-100 rounded-full h-2 overflow-hidden" data-testid="annexure-rewrite-progress-bar">
            <div
              className="h-2 bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-300"
              style={{ width: `${Math.round((rewriteProgress.done / Math.max(1, rewriteProgress.total)) * 100)}%` }}
            />
          </div>
          {rewriteProgress.currentTitles?.length > 0 && !rewriteProgress.cancelled && (
            <div className="mt-2 text-[11px] text-purple-800 leading-relaxed" data-testid="annexure-rewrite-current-titles">
              <span className="text-purple-500">Currently rewriting:</span>{' '}
              {rewriteProgress.currentTitles.slice(0, 4).map((t, i) => (
                <span key={i} className="italic">
                  {i > 0 ? ', ' : ''}“{(t || '').slice(0, 48)}”
                </span>
              ))}
              {rewriteProgress.currentTitles.length > 4 && (
                <span className="text-purple-500"> +{rewriteProgress.currentTitles.length - 4} more</span>
              )}
            </div>
          )}
        </div>
      )}

      {expanded && (
        <div className="border-t border-stone-200">
          <div className="px-4 py-2 flex items-center gap-2 bg-stone-50/50">
            <Filter size={14} className="text-stone-400" />
            <Input
              value={textFilter}
              onChange={(e) => setTextFilter(e.target.value)}
              placeholder="Filter by title, description, or category…"
              className="max-w-xs h-8 text-sm"
              data-testid="annexure-filter-input"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={selectAllVisible}
              className="h-7 text-[11px] text-stone-600"
              data-testid="annexure-select-all-visible-btn"
            >
              Select all visible
            </Button>
            <span className="text-xs text-stone-400 ml-auto">
              Click any cell to open the full editor.
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  <th className={`${HEADER_COL_STYLE} w-6 text-center`}> </th>
                  <th className={`${HEADER_COL_STYLE} w-8 text-center`}>#</th>
                  <th className={`${HEADER_COL_STYLE} w-56`}>Line Item</th>
                  {ANNEXURE_SECTIONS.map((s) => (
                    <th key={s.key} className={HEADER_COL_STYLE}>{s.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={3 + ANNEXURE_SECTIONS.length} className="px-4 py-6 text-center text-stone-400">
                      {categoryFilter
                        ? 'No items match the selected category — clear the legend chip to see everything.'
                        : 'No items to show yet — add BOQ lines above.'}
                    </td>
                  </tr>
                )}
                {rows.map(({ item, actualIndex }, rowIdx) => {
                  const parsed = parseDescription(item.description || '');
                  const id = rowId(item);
                  const isSelected = id && selectedIds.has(id);
                  const isRewritten = id && rewrittenSet.has(id);
                  return (
                    <tr
                      key={item._editId || item.id || actualIndex}
                      className={`border-b border-stone-100 hover:bg-amber-50/30 align-top ${isSelected ? 'bg-purple-50/40' : ''}`}
                      /* iter-380 — stable id so the Find/Replace bar can
                         scroll straight to this annexure row when the
                         match sits inside a description. */
                      data-testid={`annexure-item-row-${id || actualIndex}`}
                    >
                      <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => toggleSelect(id)}
                          className="h-3.5 w-3.5 accent-purple-600 cursor-pointer"
                          data-testid={`annexure-row-select-${rowIdx}`}
                          disabled={!id}
                          title={id ? 'Select for selective rewrite' : 'This row has no id yet — save the BOQ first'}
                        />
                      </td>
                      <td className="px-2 py-2 text-stone-500 text-[11px] text-center">{rowIdx + 1}</td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1.5">
                          <div className="font-medium text-stone-900 text-[12px] line-clamp-2" title={item.task_title}>
                            {item.task_title || '—'}
                          </div>
                          {isRewritten && (
                            <span
                              className="text-[9px] px-1 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-200 flex items-center gap-0.5"
                              title="AI rewrote this description in the current session"
                              data-testid={`annexure-row-rewritten-badge-${rowIdx}`}
                            >
                              <Check size={9} /> AI
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-stone-500 mt-0.5">
                          {item.main_category_name || 'Uncategorised'}
                          {item.sub_category_name ? ` • ${item.sub_category_name}` : ''}
                        </div>
                      </td>
                      {ANNEXURE_SECTIONS.map(({ key }) => {
                        const value = parsed[key] || '';
                        // iter-380 — When Find has a query, first render the
                        // markdown to HTML, then overlay <mark> highlights on
                        // top so bold/italic formatting is preserved.
                        const contentHtml = value
                          ? (findQuery
                              ? highlightHtml(
                                  markdownHtmlProps(value).__html,
                                  findQuery,
                                  { caseSensitive: findCaseSensitive, preHtml: true },
                                )
                              : markdownHtmlProps(value))
                          : null;
                        return (
                          <td
                            key={key}
                            className="px-2 py-2 border-l border-stone-100 max-w-[240px]"
                            data-testid={`annexure-cell-${rowIdx}-${key}`}
                            onClick={() => {
                              setEditingCell({ actualIndex, key });
                              setDraft(value);
                            }}
                          >
                            {value ? (
                              <div
                                className="text-[11px] whitespace-pre-wrap text-stone-700 cursor-pointer hover:bg-amber-50/60 hover:ring-1 hover:ring-amber-300 rounded p-1 min-h-[36px] transition-colors"
                                dangerouslySetInnerHTML={contentHtml}
                                title="Click to edit in a larger editor"
                              />
                            ) : (
                              <div
                                className="text-[11px] text-stone-300 italic cursor-pointer hover:bg-amber-50/60 hover:text-amber-700 hover:ring-1 hover:ring-amber-300 rounded p-1 min-h-[36px] transition-colors"
                                title="Click to add content"
                              >
                                Click to add…
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-stone-100 text-[11px] text-stone-500 flex items-center justify-between">
            <span>Cells are parsed from the item&apos;s Description. Editing here rewrites the description below.</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setTextFilter('')}
              className="text-[11px] h-6"
              data-testid="annexure-clear-filter-btn"
            >
              Clear text filter
            </Button>
          </div>
        </div>
      )}

      {/* iter-379 — full-size cell editor. Replaces the tiny inline textarea
          so the admin has plenty of room to edit multi-line spec text.
          Ctrl/Cmd+Enter saves, Escape cancels. */}
      <Dialog open={!!editingCell} onOpenChange={(v) => { if (!v) cancelDraft(); }}>
        <DialogContent
          className="max-w-3xl w-[92vw]"
          data-testid="annexure-cell-editor-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-baseline gap-2">
              <span className="text-base font-semibold">
                {editingCell ? (ANNEXURE_SECTIONS.find((s) => s.key === editingCell.key)?.title || 'Edit') : 'Edit'}
              </span>
              {editingCell && items[editingCell.actualIndex]?.task_title && (
                <span className="text-sm text-stone-500 font-normal">
                  — {items[editingCell.actualIndex].task_title}
                </span>
              )}
            </DialogTitle>
            <p className="text-xs text-stone-500 mt-1">
              One point per line. Use **bold** for materials, brands and IS codes. Ctrl/Cmd + Enter to save, Esc to cancel.
            </p>
          </DialogHeader>
          <Textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); cancelDraft(); }
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); commitDraft(); }
            }}
            className="min-h-[340px] max-h-[60vh] text-sm font-mono leading-relaxed"
            data-testid="annexure-cell-editor-textarea"
            placeholder={'- Point one\n- Point two with **brand / IS-code** highlight…'}
          />
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 text-[11px] text-stone-400 self-center">
              {draft.split(/\r?\n/).filter((l) => l.trim()).length} lines
            </div>
            <Button variant="outline" onClick={cancelDraft} data-testid="annexure-cell-editor-cancel">Cancel</Button>
            <Button onClick={commitDraft} data-testid="annexure-cell-editor-save" className="bg-amber-600 hover:bg-amber-700">
              Save cell
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter-386 — Tier update Before/After diff */}
      <Dialog open={!!tierDiff} onOpenChange={(v) => { if (!v) setTierDiff(null); }}>
        <DialogContent className="max-w-5xl w-[92vw] max-h-[85vh] overflow-hidden flex flex-col" data-testid="tier-diff-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              Review Quality Tier update — {tierDiff?.tier ? tierDiff.tier.charAt(0).toUpperCase() + tierDiff.tier.slice(1) : ''} ({tierDiff?.rows?.length || 0} row{(tierDiff?.rows?.length || 0) !== 1 ? 's' : ''})
            </DialogTitle>
            <p className="text-xs text-stone-500">
              Only Material Specification, Installation and the Vendor Rate change. Scope, Inclusions, Exclusions and Assumptions stay as-is.
            </p>
          </DialogHeader>
          <div className="overflow-auto flex-1 border border-stone-200 rounded">
            <table className="min-w-full text-xs">
              <thead className="bg-stone-50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left text-[11px] font-semibold text-stone-700 border-b w-56">Line Item</th>
                  <th className="px-2 py-2 text-left text-[11px] font-semibold text-stone-700 border-b border-l">Field</th>
                  <th className="px-2 py-2 text-left text-[11px] font-semibold text-stone-700 border-b border-l bg-red-50/40">Before</th>
                  <th className="px-2 py-2 text-left text-[11px] font-semibold text-stone-700 border-b border-l bg-emerald-50/50">After</th>
                </tr>
              </thead>
              <tbody>
                {(tierDiff?.rows || []).map(({ orig, proposal }, i) => {
                  const oldMat = _sectionText(orig.it.description, 'material');
                  const newMat = _sectionText(proposal.description, 'material');
                  const oldInst = _sectionText(orig.it.description, 'installation');
                  const newInst = _sectionText(proposal.description, 'installation');
                  const oldRate = Number(orig.it.vendor_rate) || 0;
                  const newRate = Number(proposal.vendor_rate) || 0;
                  return (
                    <tr key={i} className="border-b border-stone-100 align-top" data-testid={`tier-diff-row-${i}`}>
                      <td className="px-2 py-2 border-l">
                        <div className="font-medium text-stone-900 line-clamp-2" title={orig.it.task_title}>{orig.it.task_title}</div>
                        <div className="text-[10px] text-stone-500 mt-0.5">
                          {orig.it.main_category_name || '—'} · {orig.it.unit || 'NOS'}
                        </div>
                      </td>
                      <td className="border-l" colSpan={3}>
                        <table className="w-full text-[11px]">
                          <tbody>
                            <tr className="border-b border-stone-100">
                              <td className="px-2 py-1.5 w-24 text-stone-500">Material</td>
                              <td className="px-2 py-1.5 bg-red-50/40 whitespace-pre-wrap max-w-[280px] text-stone-700">{oldMat}</td>
                              <td className="px-2 py-1.5 bg-emerald-50/50 whitespace-pre-wrap max-w-[280px] text-stone-900 font-medium">{newMat}</td>
                            </tr>
                            <tr className="border-b border-stone-100">
                              <td className="px-2 py-1.5 w-24 text-stone-500">Installation</td>
                              <td className="px-2 py-1.5 bg-red-50/40 whitespace-pre-wrap max-w-[280px] text-stone-700">{oldInst}</td>
                              <td className="px-2 py-1.5 bg-emerald-50/50 whitespace-pre-wrap max-w-[280px] text-stone-900 font-medium">{newInst}</td>
                            </tr>
                            <tr>
                              <td className="px-2 py-1.5 w-24 text-stone-500">Vendor Rate</td>
                              <td className="px-2 py-1.5 bg-red-50/40 max-w-[280px] text-stone-700">₹{oldRate.toLocaleString('en-IN')}</td>
                              <td className="px-2 py-1.5 bg-emerald-50/50 max-w-[280px] text-stone-900 font-semibold" data-testid={`tier-diff-new-rate-${i}`}>
                                ₹{newRate.toLocaleString('en-IN')}
                                {oldRate > 0 && (
                                  <span className={`ml-1 text-[10px] ${newRate > oldRate ? 'text-red-600' : 'text-emerald-600'}`}>
                                    ({newRate > oldRate ? '+' : ''}{Math.round((newRate - oldRate) / oldRate * 100)}%)
                                  </span>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setTierDiff(null)} data-testid="tier-diff-cancel">Cancel</Button>
            <Button onClick={commitTierDiff} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tier-diff-commit">
              Apply to {tierDiff?.rows?.length || 0} row{(tierDiff?.rows?.length || 0) !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
